import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext'; // This path is correct for standard React/TypeScript setups
import { toast } from 'react-toastify';

// Define a type for UserInfo that matches what getAllUsers will return
interface UserInfo {
    _id: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    isAdmin: boolean;
    walletBalance: number;
    bankName?: string;
    bankBranch?: string;
    accountNumber?: string;
    walletId?: string;
    createdAt: string;
    lastPayoutAmount?: number; // NEW: Track last payout amount
    lastPayoutTimestamp?: string; // NEW: Track last payout timestamp
}

const ManageUsersPage: React.FC = () => {
    const { user } = useAuth();
    const [users, setUsers] = useState<UserInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filterAdmin, setFilterAdmin] = useState<string>('All'); // 'All', 'True', 'False'
    const [allUsersData, setAllUsersData] = useState<UserInfo[]>([]); // Store original fetched data

    // States for editing user wallet/payout
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [newWalletBalance, setNewWalletBalance] = useState<number | ''>('');
    const [payoutAmountInput, setPayoutAmountInput] = useState<number | ''>('');

    // Custom Tailwind animations via CSS (copied for consistency)
    const customAnimations = `
        @keyframes fade-in-down {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoom-in-out {
            0% { transform: scale(0.9); opacity: 0; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
        }
        @keyframes bounce-subtle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
        }
        @keyframes pulse-slow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        .animate-fade-in-down {
            animation: fade-in-down 0.8s ease-out forwards;
        }
        .animate-fade-in-up {
            animation: fade-in-up 0.8s ease-out forwards;
        }
        .animate-zoom-in-out {
            animation: zoom-in-out 0.5s ease-out forwards;
        }
        .animate-bounce-subtle {
            animation: bounce-subtle 1.5s infinite ease-in-out;
        }
        .animate-pulse-slow {
            animation: pulse-slow 3s infinite ease-in-out;
        }
    `;

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);

        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to view this page.');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/users`, {
                headers: {
                    'Authorization': `Bearer ${user.token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch users.');
            }

            const data: UserInfo[] = await response.json();
            setAllUsersData(data); // Store the original fetched data
            setUsers(data); // Initialize displayed users with all fetched data

        } catch (err: any) {
            console.error('Error fetching users:', err);
            setError(err.message || 'Error fetching user data.');
            toast.error(err.message || 'Error fetching user data.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Apply filters and search whenever allUsersData, searchTerm, or filterAdmin changes
    useEffect(() => {
        let filteredUsers = allUsersData;

        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            filteredUsers = filteredUsers.filter(u =>
                (u.fullName && u.fullName.toLowerCase().includes(lowerCaseSearchTerm)) ||
                (u.email && u.email.toLowerCase().includes(lowerCaseSearchTerm)) ||
                (u.phoneNumber && u.phoneNumber.includes(lowerCaseSearchTerm))
            );
        }

        if (filterAdmin !== 'All') {
            const isAdminFilter = filterAdmin === 'True';
            filteredUsers = filteredUsers.filter(u => u.isAdmin === isAdminFilter);
        }

        setUsers(filteredUsers);
    }, [allUsersData, searchTerm, filterAdmin]);


    // Handler to start editing a user's wallet
    const handleEditWallet = (userToEdit: UserInfo) => {
        setEditingUserId(userToEdit._id);
        setNewWalletBalance(userToEdit.walletBalance);
        setPayoutAmountInput(''); // Clear payout input when starting a new edit
    };

    // Handler to cancel editing
    const handleCancelEdit = () => {
        setEditingUserId(null);
        setNewWalletBalance('');
        setPayoutAmountInput('');
    };

    // Handler to update user's wallet balance
    const handleUpdateWallet = async (userId: string) => {
        if (newWalletBalance === '' || isNaN(Number(newWalletBalance))) {
            toast.error('Please enter a valid number for the new wallet balance.');
            return;
        }

        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to perform this action.');
            return;
        }

        setLoading(true); // Indicate loading for the specific action
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${userId}/update-wallet`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify({ newBalance: Number(newWalletBalance) }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update wallet balance.');
            }

            toast.success('Wallet balance updated successfully!');
            handleCancelEdit(); // Exit edit mode
            fetchUsers(); // Re-fetch all users to reflect changes
        } catch (err: any) {
            console.error('Error updating wallet:', err);
            toast.error(err.message || 'Error updating wallet balance.');
        } finally {
            setLoading(false); // End loading
        }
    };

    // Handler to process payout
    const handleProcessPayout = async (userId: string, currentWalletBalance: number) => {
        if (payoutAmountInput === '' || isNaN(Number(payoutAmountInput)) || Number(payoutAmountInput) <= 0) {
            toast.error('Please enter a valid positive number for the payout amount.');
            return;
        }

        const amountToPayout = Number(payoutAmountInput);

        if (amountToPayout > currentWalletBalance) {
            toast.error('Payout amount cannot exceed current wallet balance.');
            return;
        }

        if (!user || !user.token || !user.isAdmin) {
            toast.error('Authentication error: Not authorized to perform this action.');
            return;
        }

        setLoading(true); // Indicate loading for the specific action
        try {
            // For simplicity, this will be a direct deduction from wallet.
            // A robust system would involve creating a withdrawal payment request.
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${userId}/payout`, {
                method: 'POST', // or PUT, depending on your backend design for this action
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify({ amount: amountToPayout }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to process payout.');
            }

            toast.success(`Payout of Rs. ${amountToPayout.toFixed(2)} processed successfully!`);
            handleCancelEdit(); // Exit edit mode
            fetchUsers(); // Re-fetch all users to reflect changes
        } catch (err: any) {
            console.error('Error processing payout:', err);
            toast.error(err.message || 'Error processing payout.');
        } finally {
            setLoading(false); // End loading
        }
    };


    if (loading && !allUsersData.length) { // Only show full loading screen on initial load
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-200 font-inter">
                <style>{customAnimations}</style>
                <div className="text-center">
                    <p className="text-2xl font-semibold text-purple-700 animate-pulse-slow">
                        Loading user data...
                    </p>
                    <div className="mt-4 w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100 font-inter">
                <style>{customAnimations}</style>
                <div className="bg-white p-8 rounded-lg shadow-xl border-t-8 border-red-500 w-full max-w-md text-center animate-zoom-in-out">
                    <h2 className="text-3xl font-bold text-red-700 mb-4">Error!</h2>
                    <p className="text-lg text-gray-700">{error}</p>
                    <button
                        onClick={fetchUsers}
                        className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 shadow-md"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!user || !user.isAdmin) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100 font-inter">
                <style>{customAnimations}</style>
                <div className="bg-white p-8 rounded-lg shadow-xl border-t-8 border-red-500 w-full max-w-md text-center animate-zoom-in-out">
                    <h2 className="text-3xl font-bold text-red-700 mb-4">Access Denied!</h2>
                    <p className="text-lg text-gray-700">You do not have administrative privileges to view this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 md:p-8 min-h-screen font-inter bg-gradient-to-br from-blue-100 to-purple-200">
            <style>{customAnimations}</style> {/* Inject custom animations */}

            <h1 className="text-4xl font-extrabold text-center mb-10 text-indigo-800 animate-fade-in-down">
                👥 Manage Users 👥
            </h1>

            {/* Filter and Search Section */}
            <div className="bg-white p-6 rounded-2xl shadow-3xl mb-8 border-t-8 border-purple-400 animate-zoom-in-out">
                <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">Filter & Search Users</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label htmlFor="searchTerm" className="block text-gray-700 text-lg font-bold mb-2">Search (Name / Email / Phone):</label>
                        <input
                            type="text"
                            id="searchTerm"
                            className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out text-lg shadow-sm"
                            placeholder="User Name, Email, or Phone Number"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div>
                        <label htmlFor="filterAdmin" className="block text-gray-700 text-lg font-bold mb-2">Filter by Admin Status:</label>
                        <select
                            id="filterAdmin"
                            className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 ease-in-out text-lg shadow-sm bg-white"
                            value={filterAdmin}
                            onChange={(e) => setFilterAdmin(e.target.value)}
                        >
                            <option value="All">All Users</option>
                            <option value="True">Admins Only</option>
                            <option value="False">Non-Admins Only</option>
                        </select>
                    </div>
                </div>
                {/* Note: No explicit "Apply Filters" button needed as filters apply automatically */}
                <p className="text-sm text-gray-500 text-center">Filters apply automatically as you type or select.</p>
            </div>

            {/* User List */}
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                All Users ({users.length})
            </h2>
            {users.length === 0 ? (
                <p className="text-center text-gray-600 text-lg py-8">No users found matching the current filters.</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {users.map(userItem => (
                        <div
                            key={userItem._id}
                            className={`relative bg-white p-6 rounded-xl shadow-lg border-t-4 transform transition-transform duration-300 hover:scale-[1.02] hover:shadow-2xl animate-fade-in-up
                                ${userItem.isAdmin ? 'border-red-500' : 'border-blue-500'}`}
                        >
                            {/* Admin Badge */}
                            {userItem.isAdmin && (
                                <div className="absolute top-0 right-0 m-3 px-3 py-1 rounded-full bg-red-600 text-white text-xs font-bold uppercase shadow-md">
                                    Admin
                                </div>
                            )}

                            <h3 className="text-2xl font-bold text-indigo-700 mb-2 mt-2">{userItem.fullName}</h3>
                            <p className="text-md text-gray-700 mb-1">
                                <span className="font-semibold">Email:</span> {userItem.email}
                            </p>
                            <p className="text-md text-gray-700 mb-1">
                                <span className="font-semibold">Phone:</span> {userItem.phoneNumber}
                            </p>
                            {/* Wallet Balance Display and Edit Section */}
                            {editingUserId === userItem._id ? (
                                <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    <label htmlFor={`wallet-${userItem._id}`} className="block text-gray-700 text-sm font-semibold mb-2">Adjust Wallet Balance:</label>
                                    <input
                                        type="number"
                                        id={`wallet-${userItem._id}`}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-md shadow-sm mb-3"
                                        value={newWalletBalance}
                                        onChange={(e) => setNewWalletBalance(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                        placeholder="New balance"
                                    />
                                    <button
                                        onClick={() => handleUpdateWallet(userItem._id)}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 shadow-md text-sm mb-2"
                                        disabled={loading}
                                    >
                                        {loading ? 'Updating...' : 'Update Wallet'}
                                    </button>

                                    <label htmlFor={`payout-${userItem._id}`} className="block text-gray-700 text-sm font-semibold mt-4 mb-2">Process Payout:</label>
                                    <input
                                        type="number"
                                        id={`payout-${userItem._id}`}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-md shadow-sm mb-3"
                                        value={payoutAmountInput}
                                        onChange={(e) => setPayoutAmountInput(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                        placeholder="Payout amount"
                                    />
                                    <button
                                        onClick={() => handleProcessPayout(userItem._id, userItem.walletBalance)}
                                        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 shadow-md text-sm mb-2"
                                        disabled={loading}
                                    >
                                        {loading ? 'Processing...' : 'Process Payout'}
                                    </button>

                                    <button
                                        onClick={handleCancelEdit}
                                        className="w-full bg-gray-400 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-md transition duration-300 shadow-md text-sm mt-2"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-md text-gray-700">
                                        <span className="font-semibold">Wallet:</span> Rs. {userItem.walletBalance.toFixed(2)}
                                    </p>
                                    <button
                                        onClick={() => handleEditWallet(userItem)}
                                        className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-1.5 px-3 rounded-md transition duration-300 shadow-md text-sm"
                                    >
                                        Edit Wallet/Payout
                                    </button>
                                </div>
                            )}

                            {/* Payout Information */}
                            {userItem.lastPayoutAmount !== undefined && userItem.lastPayoutAmount > 0 && userItem.lastPayoutTimestamp && (
                                <div className="mt-3 p-2 bg-yellow-100 rounded-md border border-yellow-300 text-sm text-yellow-800">
                                    <p><span className="font-semibold">Last Payout:</span> Rs. {userItem.lastPayoutAmount.toFixed(2)}</p>
                                    <p><span className="font-semibold">Date:</span> {new Date(userItem.lastPayoutTimestamp).toLocaleDateString()}</p>
                                </div>
                            )}

                            {userItem.bankName && (
                                <p className="text-sm text-gray-600 mt-2 mb-1">
                                    <span className="font-semibold">Bank:</span> {userItem.bankName}
                                </p>
                            )}
                            {userItem.bankBranch && (
                                <p className="text-sm text-gray-600 mb-1">
                                    <span className="font-semibold">Branch:</span> {userItem.bankBranch}
                                </p>
                            )}
                            {userItem.accountNumber && (
                                <p className="text-sm text-gray-600 mb-1">
                                    <span className="font-semibold">Account:</span> {userItem.accountNumber}
                                </p>
                            )}
                             {userItem.walletId && (
                                <p className="text-sm text-gray-600 mb-1">
                                    <span className="font-semibold">Wallet ID:</span> {userItem.walletId}
                                </p>
                            )}
                            <p className="text-sm text-gray-500 mt-2">
                                Joined: {new Date(userItem.createdAt).toLocaleDateString()}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ManageUsersPage;